// src/pages/Cloud/components/fsc/FSCEditClauseSidebar.js
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Offcanvas, Card, Button, ListGroup, Badge } from 'react-bootstrap';

import FSCEditClauseItemSidebar from './FSCEditClauseItemSidebar';
import FSCEditClauseSelectorBlock from './FSCEditClauseSelectorBlock';

import { buildNodeIndex, getPathLabel, statusLabel } from './fscEditClauseUtils';
import { refreshFscProjectData } from './useFscProjectBootstrap';
import {
  bulkEditFSCClauses,
} from '../../../../store/slices/fscComplianceSlice';
import toastAlert from '../../../../js/Base/ToastAlert';

function StatusBadge({ status }) {
  const text = statusLabel(status);
  return (
    <Badge bg="light" text="dark" style={{ border: '1px solid #e5e5e5', fontWeight: 'bold' }}>
      {text}
    </Badge>
  );
}

function FSCClauseDraftList({ drafts = [], onOpen, onRemove }) {
  if (!drafts || drafts.length === 0) {
    return <div style={{ color: '#999', fontSize: 13 }}>尚無條款</div>;
  }

  return (
    <ListGroup>
      {drafts.map((d) => {
        const key = d?.key;
        const pathLabel = getPathLabel(d?.pathNodes);
        const name = String(d?.edits?.control_name || '').trim() || '(未命名)';
        const st = d?.edits?.status || null;

        return (
          <ListGroup.Item
            key={key}
            action
            onClick={() => onOpen && onOpen(d)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {name}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: '#666',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {pathLabel}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <StatusBadge status={st} />
              <Button
                variant="text"
                size="sm"
                style={{ fontWeight: 'bold' }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRemove && onRemove(key);
                }}
              >
                刪除
              </Button>
            </div>
          </ListGroup.Item>
        );
      })}
    </ListGroup>
  );
}

/**
 * （第一層）：
 * - 初始只有一組「逐層下拉 selector」
 * - 選到葉節點（is_assessable===true）才在該最末層右側顯示「編輯/刪除」
 * - 點「編輯」才開第二層（FSCEditClauseItemSidebar）
 * - 第二層儲存後：新增/更新草稿，第一層不自動顯示下一組下拉
 * - 只有在「當前沒有下拉選單」時才顯示「編輯更多條款」按鈕；點了才產生下一組下拉（出現在草稿下方）
 * - 草稿點擊：直接開第二層（不回填第一層下拉）
 * - 刪除：草稿刪除只刪前端；selector 的刪除若該 leaf 無草稿則清空當前 selector 選取
 */
export default function FSCEditClauseSidebar({ show, onHide, projectUuid, tree, monthKey, cloud, cloudScope }) {
  const dispatch = useDispatch();
  const { assessments: fscAssessments } = useSelector((state) => state.fscCompliance);

  const index = useMemo(() => buildNodeIndex(tree || []), [tree]);

  // 草稿：以 leaf pk 作為唯一鍵
  const [draftMap, setDraftMap] = useState(() => new Map());
  const draftScopeKey = [projectUuid || '', cloud || '', cloudScope || ''].join('|');

  // 第二層 sidebar（編輯細項）
  const [showItemSidebar, setShowItemSidebar] = useState(false);
  const [editingLeafPk, setEditingLeafPk] = useState(null);

  // 第一層：是否顯示「當前 selector block」
  const [showSelector, setShowSelector] = useState(true);
  const [selectorKey, setSelectorKey] = useState(1); // 用於 remount/reset selector

  // 草稿只屬於建立當下的 project/cloud/scope。範圍切換時立即作廢，
  // 避免舊 control UUID 搭配新 cloud_scope 造成跨範圍部分寫入。
  useEffect(() => {
    setDraftMap(new Map());
    setShowItemSidebar(false);
    setEditingLeafPk(null);
    setShowSelector(true);
    setSelectorKey((value) => value + 1);
  }, [draftScopeKey]);

  const drafts = useMemo(() => Array.from(draftMap.values()), [draftMap]);

  const hideItemSidebar = ({ keepSelector = false } = {}) => {
    setShowItemSidebar(false);
    setEditingLeafPk(null);
    setShowSelector(keepSelector);
  };

  const resetToSingleSelector = () => {
    setShowSelector(true);
    setSelectorKey((v) => v + 1);
  };

  const openItemSidebarByLeafPk = (leafPk) => {
    if (!leafPk) return;
    const leafNode = index.byPk.get(String(leafPk))?.node;
    if (leafNode?.read_only) {
      toastAlert.autoClose(true).showError('未分派範圍的舊條款僅供檢視，無法編輯');
      return;
    }
    setEditingLeafPk(leafPk);
    setShowItemSidebar(true);
    // 開第二層時先隱藏 selector，改由「編輯更多條款」控制新增下一組
    setShowSelector(false);
  };

  const handleClose = () => {
    onHide && onHide();

    // 關閉不強制清草稿
    hideItemSidebar({ keepSelector: true });
    resetToSingleSelector();
  };

  const removeDraft = (leafPk) => {
    const pk = String(leafPk || '').trim();
    if (!pk) return;

    setDraftMap((prev) => {
      const next = new Map(prev);
      next.delete(pk);
      return next;
    });
  };

  const upsertDraft = ({ leafPk, edits }) => {
    const key = String(leafPk || '').trim();
    const record = index.byPk.get(key);
    const leafNode = record?.node;
    const pathNodes = record?.path || [];

    if (!leafNode) {
      toastAlert.autoClose(true).showError('找不到條款節點，請重新選擇');
      return false;
    }
    if (leafNode.read_only) {
      toastAlert.autoClose(true).showError('未分派範圍的舊條款僅供檢視，無法編輯');
      return false;
    }

    setDraftMap((prev) => {
      const next = new Map(prev);
      next.set(key, {
        key,
        scopeKey: draftScopeKey,
        leafPk: key,
        leafNode,
        pathNodes,
        edits: {
          control_name: edits.control_name,
          status: edits.status ?? null,
          recommendation: edits.recommendation ?? '',
          remediation_steps: edits.remediation_steps ?? '',
        },
      });
      return next;
    });
    return true;
  };

  // 第一層 selector：點「編輯」
  const openItemSidebarForLeaf = (leafPk) => {
    openItemSidebarByLeafPk(leafPk);
  };

  // 第一層 selector：點「刪除」
  const handleSelectorDelete = ({ leafPk, hasDraft, clearSelection }) => {
    if (index.byPk.get(String(leafPk))?.node?.read_only) {
      toastAlert.autoClose(true).showError('未分派範圍的舊條款僅供檢視，無法刪除');
      return;
    }
    if (hasDraft) {
      removeDraft(leafPk);
      return;
    }
    // 沒草稿就清掉 selector 的選取
    if (typeof clearSelection === 'function') clearSelection();
  };

  // 草稿點擊：直接開第二層（不回填第一層下拉）
  const openItemSidebarForDraft = (draft) => {
    openItemSidebarByLeafPk(draft?.leafPk);
  };

  const currentDraft = useMemo(() => {
    if (!editingLeafPk) return null;
    return draftMap.get(String(editingLeafPk)) || null;
  }, [editingLeafPk, draftMap]);

  const editingInitialStatus = useMemo(() => {
    if (!editingLeafPk) return null;

    const record = index.byPk.get(String(editingLeafPk));
    const controlId = record?.node?.control_id;
    const controlUuid = String(record?.node?.uuid || record?.node?.id || '').trim();
    if (!controlUuid && !controlId) return null;

    const hit = (fscAssessments || []).find((a) => {
      const assessmentControlUuid = String(a?.control_uuid || '').trim();
      const sameControl = assessmentControlUuid
        ? assessmentControlUuid === controlUuid
        : String(a?.control_id) === String(controlId);
      const sameMonth = String(a?.assessment_month) === String(monthKey);
      const sameCloud =
        !a?.cloud_provider || String(a.cloud_provider).toLowerCase() === String(cloud || '').toLowerCase();
      return sameControl && sameMonth && sameCloud;
    });

    const s = hit?.status ? String(hit.status).toLowerCase() : null;
    return s || null;
  }, [editingLeafPk, index, fscAssessments, monthKey, cloud]);

  const handleSaveFromItemSidebar = ({ leafPk, edits }) => {
    if (!leafPk) return;
    if (!upsertDraft({ leafPk, edits })) return;

    // 第二層關閉
    // 儲存後：不要自動顯示下一組 selector，改由使用者手動新增
    hideItemSidebar({ keepSelector: false });
  };

  const openNewSelector = () => {
    setShowSelector(true);
    setSelectorKey((v) => v + 1);
  };

  const handleOpenDraft = (draft) => openItemSidebarForDraft(draft);
  const handleRemoveDraft = (key) => removeDraft(key);
  const handleEditLeaf = (leafPk) => openItemSidebarForLeaf(leafPk);
  const handleHideItemSidebar = () => hideItemSidebar({ keepSelector: false });

  const handleSubmitAll = async () => {
    if (!projectUuid) {
      toastAlert.autoClose(true).showError('缺少 project_uuid');
      return;
    }
    if (drafts.length === 0) {
      toastAlert.autoClose(true).showError('目前沒有草稿可送出');
      return;
    }

    if (!cloud) {
      toastAlert.autoClose(true).showError('缺少 cloud 參數');
      return;
    }
    if (drafts.some((draft) => draft?.scopeKey !== draftScopeKey)) {
      setDraftMap(new Map());
      toastAlert.autoClose(true).showError('查詢範圍已變更，舊草稿已清除，請重新編輯');
      return;
    }
    if (
      drafts.some((draft) => {
        const currentNode = index.byPk.get(String(draft?.leafPk || ''))?.node;
        return Boolean(draft?.leafNode?.read_only || currentNode?.read_only);
      })
    ) {
      toastAlert.autoClose(true).showError('草稿包含未分派範圍的唯讀條款，請移除後再送出');
      return;
    }

    try {
      await dispatch(
        bulkEditFSCClauses({
          projectUuid,
          cloud,
          cloud_scope: cloudScope,
          monthKey,
          drafts,
        })
      ).unwrap();

      await refreshFscProjectData(dispatch, projectUuid, cloud, {
        unwrap: true,
        platform: cloud,
        cloudScope,
      });

      toastAlert.autoClose(true).showSuccess('編輯成功');

      // 成功後清空草稿並關閉
      setDraftMap(new Map());
      hideItemSidebar({ keepSelector: true });
      resetToSingleSelector();

      onHide && onHide();
    } catch (err) {
      const msg = typeof err === 'string' ? err : err?.detail || err?.message || '編輯條款失敗';
      toastAlert.autoClose(true).showError(msg);
    }
  };

  return (
    <>
      <Offcanvas show={show} onHide={handleClose} placement="end" backdrop="static" style={{ width: '75%' }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ fontWeight: 'bold' }}>編輯條款</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body style={{ display: 'flex', flexDirection: 'column', padding: 0 }}>
          <div style={{ flex: 1, padding: 16, overflowY: 'auto' }}>
            <Card>
              <Card.Header style={{ fontWeight: 'bold' }}>條款</Card.Header>
              <Card.Body>
                {/* 草稿（永遠在上方） */}
                <div style={{ marginTop: 12 }}>
                  <FSCClauseDraftList
                    drafts={drafts}
                    onOpen={handleOpenDraft}
                    onRemove={handleRemoveDraft}
                  />
                </div>

                {/* selector：只有 showSelector=true 時顯示，且出現在草稿下方（符合「逐項往下」） */}
                {showSelector ? (
                  <div style={{ marginTop: 16 }}>
                    <FSCEditClauseSelectorBlock
                      key={`selector-${selectorKey}`}
                      tree={Array.isArray(tree) ? tree : []}
                      draftsByLeafPk={draftMap}
                      onEditLeaf={handleEditLeaf}
                      onDeleteLeafOrClearSelection={handleSelectorDelete}
                    />
                  </div>
                ) : (
                  // 沒有 selector 的時候才顯示「編輯更多條款」
                  <div style={{ marginTop: 16 }}>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      style={{ fontWeight: 'bold' }}
                      onClick={openNewSelector}
                      disabled={showItemSidebar}
                    >
                      編輯更多條款
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Footer */}
          <div
            style={{
              borderTop: '1px solid #e5e5e5',
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 10,
              background: '#fff',
            }}
          >
            <Button className="btn btn-cancel" variant="secondary" onClick={handleClose}>
              取消
            </Button>
            <Button className="btn btn-custom" variant="primary" onClick={handleSubmitAll}>
              儲存所有條款
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* 第二層：編輯條款細項 */}
      <FSCEditClauseItemSidebar
        show={showItemSidebar}
        onHide={handleHideItemSidebar}
        tree={Array.isArray(tree) ? tree : []}
        initialLeafPk={editingLeafPk || null}
        initialDraft={currentDraft}
        initialStatus={editingInitialStatus}
        onSave={handleSaveFromItemSidebar}
      />
    </>
  );
}
